<script setup>
import { toRaw } from 'vue';
import Draggable from 'vuedraggable';
import ConditionRow from './ConditionRow.vue';
import { moveCondition } from './helper/conditionOrder';

defineProps({
  filterTypes: { type: Array, required: true },
});

const emit = defineEmits(['remove']);

const filters = defineModel('filters', {
  type: Array,
  required: true,
});

// Rows need stable keys to be dragged, but the filters are sent to the API as
// they are, so the ids live here instead of on the filter objects.
const rowIds = new WeakMap();
let lastRowId = 0;
const rowKey = filter => {
  const rawFilter = toRaw(filter);
  if (!rowIds.has(rawFilter)) {
    lastRowId += 1;
    rowIds.set(rawFilter, lastRowId);
  }
  // A new attribute re-mounts the row, so its inputs start from scratch.
  return `filter-${rowIds.get(rawFilter)}-${filter.attributeKey}`;
};

// A ref inside the draggable item slot is not collected into an array, so the
// rows register themselves under their key.
const rows = new Map();
const registerRow = key => row => {
  if (row) {
    rows.set(key, row);
  } else {
    rows.delete(key);
  }
};

const validate = () =>
  filters.value.every(filter => rows.get(rowKey(filter))?.validate() ?? true);

const onChange = ({ moved }) => {
  if (!moved) return;
  filters.value = moveCondition(filters.value, moved.oldIndex, moved.newIndex);
};

const updateQueryOperator = (index, value) => {
  filters.value[index - 1].queryOperator = value;
};

// AND binds before OR, so every OR row starts a new group of conditions.
// A line above it shows where one group ends and the next one begins.
const startsGroup = index =>
  index > 0 && filters.value[index - 1].queryOperator === 'or';

defineExpose({ validate });
</script>

<template>
  <Draggable
    :model-value="filters"
    :item-key="rowKey"
    tag="ul"
    class="grid gap-4 list-none"
    handle=".condition-drag-handle"
    ghost-class="opacity-50"
    :animation="150"
    :disabled="filters.length < 2"
    @change="onChange"
  >
    <template #item="{ element: filter, index }">
      <ConditionRow
        :ref="registerRow(rowKey(filter))"
        v-model:attribute-key="filter.attributeKey"
        v-model:filter-operator="filter.filterOperator"
        v-model:values="filter.values"
        :class="{ 'border-t border-n-weak pt-4': startsGroup(index) }"
        :query-operator="
          index > 0 ? filters[index - 1].queryOperator : undefined
        "
        :show-query-operator="index > 0"
        :show-drag-handle="filters.length > 1"
        :filter-types="filterTypes"
        @update:query-operator="updateQueryOperator(index, $event)"
        @remove="emit('remove', index)"
      />
    </template>
  </Draggable>
</template>
